import { useState, useRef, useEffect } from 'preact/hooks';
import { UserIcon, ShoppingCartIcon, Bars3Icon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { logNavInteraction, NAV_LOCATIONS, NAV_SECTIONS, NAV_ELEMENT_TYPES, NAV_ACTIONS } from 'utils';
import NavDrawer from './NavDrawer';
import SubDrawer from './SubDrawer';
import DropdownMenuItem from './DropdownMenuItem';
import { useScreenSize, SCREEN_SIZES } from 'react-hooks/useScreenSize';
import { LinkButton, BUTTON_SIZES, BUTTON_SCHEMES } from 'react-components/LinkButton';
import  AnnouncementBar  from 'react-components/AnnouncementBar';

const BASE_NAV_INTERACTION_CONFIG = {
  location: NAV_LOCATIONS.PRIMARY, 
  depth: 0, 
}

const Header = ({ customer, visible, isBundleBuilder, redirectCartToIndex, menuItems, announcementMessages}) => {
  const [drawerState, setDrawerState] = useState({
    nav: false,
    sub: false
  });

  const [openMenu, setOpenMenu] = useState(null);
  const [hoverMenu, setHoverMenu] = useState(null); 
  const dropdownRef = useRef(null);
  const [resetScrollFn, setResetScrollFn] = useState(null);
  const [isClickTriggered, setIsClickTriggered] = useState(false); 

  const { screenSize } = useScreenSize();
  const isMobile = screenSize === SCREEN_SIZES.MOBILE;
  const isTablet = screenSize === SCREEN_SIZES.TABLET;

  const navDrawerRef = useRef(null);
  const subDrawerRef = useRef(null);

  const handleNavToggle = (navInteractionConfig) => {
    setDrawerState(prev => ({
      nav: !prev.nav,
      sub: false
    }));

    logNavInteraction({
      ...BASE_NAV_INTERACTION_CONFIG,
      ...navInteractionConfig,
    });
  };

  const handleMenuToggle = (menu, navInteractionConfig) => {    
    // If clicking a different menu than the currently open one, close all menus
    if (openMenu && openMenu !== menu) {
      setOpenMenu(null);
      setHoverMenu(null);
      setIsClickTriggered(false);
      return;
    }

    // Otherwise, toggle the menu normally
    setIsClickTriggered(true);
    setOpenMenu(openMenu === menu ? null : menu);
    setHoverMenu(null);

    logNavInteraction({
      ...BASE_NAV_INTERACTION_CONFIG,
      ...navInteractionConfig,
    });
  };

  const handleMenuHover = (menu, navInteractionConfig) => {
    if (!isClickTriggered && openMenu) {
      setHoverMenu(menu);
    } else if (openMenu) {
      setHoverMenu(menu);
    }

    if (openMenu && navInteractionConfig) {
      logNavInteraction({
        ...BASE_NAV_INTERACTION_CONFIG,
        ...navInteractionConfig,
      });
    }
  };

  const handleMenuLeave = () => {
    if (!isClickTriggered) {
      setHoverMenu(null);
    }
  };

  const closeAllMenus = () => {
    setOpenMenu(null);
    setHoverMenu(null);
    setIsClickTriggered(false);
  };

  const [activeSubDrawerKey, setActiveSubDrawerKey] = useState(null);

  const openSubDrawer = (subDrawerKey) => {
    setActiveSubDrawerKey(subDrawerKey);
    setDrawerState(prev => ({ ...prev, sub: true }));
  };

  const closeSubDrawer = () => {
    setDrawerState(prev => ({ ...prev, sub: false }));
  };

  const closeBothDrawers = () => {
    if (resetScrollFn) {
      resetScrollFn();
    }
    setDrawerState({ nav: false, sub: false });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedOutsideNav = drawerState.nav && navDrawerRef.current && !navDrawerRef.current.contains(event.target);
      const clickedOutsideSub = drawerState.sub && subDrawerRef.current && !subDrawerRef.current.contains(event.target);
      const clickedOutsideDropdown = (openMenu || hoverMenu) && dropdownRef.current && !dropdownRef.current.contains(event.target);

      if (clickedOutsideNav) {
        setDrawerState(prev => ({ ...prev, nav: false }));
      }
      if (clickedOutsideSub) {
        setDrawerState(prev => ({ ...prev, sub: false }));
      }
      if (clickedOutsideDropdown) {
        closeAllMenus();
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [drawerState.nav, drawerState.sub, openMenu, hoverMenu]);

  useEffect(() => {
    if (visible === false && isBundleBuilder) {
      closeAllMenus();
    }
  }, [visible, isBundleBuilder]);

  const currentMenu = (
    isMobile || isTablet 
      ? (activeSubDrawerKey)
      : (hoverMenu || openMenu)
  )?.toLowerCase().trim();
  
  const activeMenuItem = menuItems.find(item => item.label.toLowerCase() === currentMenu);
  
  return (
    <>
      <AnnouncementBar announcementMessages={announcementMessages} />

      <nav className="tw-relative tw-z-10 tw-mx-auto tw-flex tw-min-h-[60px] tw-max-w-screen-desktop tw-items-center tw-justify-between tw-bg-white tw-px-16 tw-shadow-[0px_2px_6px_0px_rgba(16,_24,_40,_0.06)] tablet:max-desktop:tw-pl-48 tablet:max-desktop:tw-pr-32 desktop:tw-h-[80px] desktop:tw-shadow-none" aria-label="Main navigation">
      {(isMobile || isTablet) && (
        <LinkButton
          classNames="tablet:tw-ml-0 !tw-p-0 tw-h-[44px] tw-w-[126px] tw-capitalize tw-text-12 tablet:tw-text-14 tw-bg-[linear-gradient(15deg,_#002F65_0%,_#005EB8_15%,_#0075C1_47.13%,_#0089CF_55.25%,_#00A3E0_80.04%,_#66C6FC_100%)]" 
          href={'/pages/bundle-builder'}
          size={BUTTON_SIZES.MEDIUM}
          scheme={BUTTON_SCHEMES.PRIMARY}
          onClick={() => logNavInteraction({
              ...BASE_NAV_INTERACTION_CONFIG, 
              section: NAV_SECTIONS.PRIMARY.TOP_BAR.CTA, 
              order: 1, 
              elementType: NAV_ELEMENT_TYPES.BUTTON, 
              text: 'Build a Bundle', 
              action: NAV_ACTIONS.LINK,
            })}
          >
            Build a Bundle
        </LinkButton>
    )}
        <div className="tw-z-[2] tw-flex tw-items-center tw-justify-between">
          <a href="/" onClick={() => logNavInteraction({
              ...BASE_NAV_INTERACTION_CONFIG,
              section: NAV_SECTIONS.PRIMARY.TOP_BAR.LOGO, 
              order: 1, 
              elementType: NAV_ELEMENT_TYPES.IMAGE, 
              text: 'Cirkul', 
              action: NAV_ACTIONS.LINK,
          })} 
          className="tw-box-content tw-h-[24px] tw-p-8 tablet:tw-h-auto">
            <img
            className='tw-h-full tw-w-auto tw-object-contain'
              src="https://cdn.shopify.com/s/files/1/0050/5217/0330/files/2021-header-logo-blue.svg?v=1683062684"
              alt="Cirkul"
            />
          </a>
        </div>

        {/* Desktop Navigation Links */}
        <div className="tw-hidden tw-items-center tw-justify-between tw-self-stretch desktop:tw-flex" role="navigation" aria-label="Primary">
          <div className="tw-flex tw-size-full tw-justify-between tw-gap-48 tw-px-[7.5rem] tablet:tw-w-full tablet:tw-flex-row tablet:tw-items-center tablet:tw-justify-between tablet:tw-p-0">
            <a href="/pages/bundle-builder" aria-label="Build a Bundle" onClick={() => logNavInteraction({
              ...BASE_NAV_INTERACTION_CONFIG, 
              section: NAV_SECTIONS.PRIMARY.TOP_BAR.CTA, 
              order: 1, 
              elementType: NAV_ELEMENT_TYPES.BUTTON, 
              text: 'Build a Bundle', 
              action: NAV_ACTIONS.LINK,
            })}
              class="tw-relative tw-z-[2] tw-inline-block tw-overflow-hidden tw-rounded-4 tw-px-24 tw-py-14 tw-text-14 tw-font-bold tw-capitalize tw-leading-16 tw-text-white after:tw-absolute
                      after:tw-left-0 after:tw-top-0 after:tw--z-[1] after:tw-h-[120px] after:tw-w-[150px] after:tw-translate-y-[-70px] after:tw-bg-[linear-gradient(15deg,_#002F65_0%,_#005EB8_15%,_#0075C1_47.13%,_#0089CF_55.25%,_#00A3E0_80.04%,_#66C6FC_100%)]
                      after:tw-transition-transform
                      after:tw-duration-200 after:tw-ease-in after:tw-content-[''] hover:tw-animate-pulse512 hover:after:tw-translate-y-0">
              Build a Bundle
            </a>
            <ul className="tw-flex tw-h-full tw-flex-col tw-gap-16 tablet:tw-flex-row">
              {Object.values(menuItems).map((link, index) => (
                <li
                  key={index}
                  className="tw-flex tw-h-full tw-items-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMenuToggle(link.label, {
                      section: NAV_SECTIONS.PRIMARY.TOP_BAR.MAIN, 
                      order: index + 1, 
                      elementType: NAV_ELEMENT_TYPES.TEXT, 
                      text: link.label, 
                      action: NAV_ACTIONS.OPEN_SUBMENU,
                    });
                  }}
                >
                  <div className="tw-group tw-relative tw-flex tw-h-[44px] tw-items-center tw-overflow-visible">
                    {link.category_groups.length > 0 || link.featured_products.length > 0 ? (
                      <button
                        aria-expanded={!!currentMenu}
                        aria-controls={`dropdown-${link.label.toLowerCase().replace(' ', '-')}`}
                        aria-label={`${link.label} menu`}
                        tabIndex="0"
                        role="button"
                        onMouseEnter={() => handleMenuHover(link.label, {
                          section: NAV_SECTIONS.PRIMARY.TOP_BAR.MAIN, 
                          order: index + 1, 
                          elementType: NAV_ELEMENT_TYPES.TEXT, 
                          text: link.label, 
                          action: NAV_ACTIONS.OPEN_SUBMENU,
                        })}
                        onMouseLeave={handleMenuLeave}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMenuToggle(link.label, {
                            section: NAV_SECTIONS.PRIMARY.TOP_BAR.MAIN, 
                            order: index + 1, 
                            elementType: NAV_ELEMENT_TYPES.TEXT, 
                            text: link.label, 
                            action: NAV_ACTIONS.OPEN_SUBMENU,
                          });
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleMenuToggle(link.label, {
                              section: NAV_SECTIONS.PRIMARY.TOP_BAR.MAIN, 
                              order: index + 1, 
                              elementType: NAV_ELEMENT_TYPES.TEXT, 
                              text: link.label, 
                              action: NAV_ACTIONS.OPEN_SUBMENU,
                            });
                          }
                        }}
                        className={`tw-relative tw-flex tw-h-[44px] tw-w-full tw-items-center tw-justify-between tw-gap-8 tw-pl-6 tw-text-center tw-text-16 tw-font-semibold tw-leading-16 ${
                          currentMenu === link.label ? 'tw-text-[#3F4443]' : 'tw-text-[#3F4443]'
                        }`}
                      >
                        <span>{link.label}</span>
                        <span className="tw-relative tw-inline-flex tw-size-16 tw-font-semibold">
                          <ChevronUpIcon
                            className={`tw-absolute tw-transition-opacity tw-duration-200 ${
                              currentMenu === link.label.toLowerCase().trim() ? 'tw-opacity-100' : 'tw-opacity-0'
                            }`}
                          />
                          <ChevronDownIcon
                            className={`tw-absolute tw-transition-opacity tw-duration-200 ${
                              currentMenu !== link.label.toLowerCase().trim() ? 'tw-opacity-100' : 'tw-opacity-0'
                            }`}
                          />
                        </span>
                      </button>
                    ) : (
                      <a
                        href={link.link}
                        tabIndex="0"
                        role="button"
                        onClick={() => logNavInteraction({
                          ...BASE_NAV_INTERACTION_CONFIG,
                          section: NAV_SECTIONS.PRIMARY.TOP_BAR.MAIN, 
                          order: index + 1, 
                          elementType: NAV_ELEMENT_TYPES.TEXT, 
                          text: link.label, 
                          action: NAV_ACTIONS.LINK,
                        })}
                        className={`tw-group tw-flex tw-h-[44px] tw-w-full tw-items-center tw-justify-between tw-gap-8 tw-px-6 tw-text-center tw-text-16 tw-font-semibold tw-leading-16 !tw-transition-all ${
                          currentMenu === link.label ? 'tw-text-[#3F4443]' : 'tw-text-[#3F4443]'
                        }`}
                      >
                        {link.label}
                      </a>
                    )}
                      <span
                      className={`
                        tw-absolute tw-bottom-0 tw-left-0 tw-h-[3px] tw-origin-left
                        tw-transform tw-bg-[#00A3E0]
                        ${currentMenu === link.label.toLowerCase().trim() ? 'tw-scale-x-100' : 'tw-scale-x-0'}
                        tw-transition-transform
                        tw-duration-300 tw-ease-out group-hover:tw-scale-x-100
                      `}
                      style={{
                        width: '100%', 
                        transformOrigin: 'left center', 
                      }}
                    ></span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Right-side Icons and Mobile Toggle */}
        <div className='tw-z-[2] tw-flex tw-justify-end tablet:tw-gap-12'>
          {/* User and Cart Icons */}
          <ul className="tw-flex tw-items-center tw-gap-[20px] tablet:tw-flex-row tablet:tw-gap-4">
            <li className='tw-hidden tw-gap-[22px] tablet:tw-items-center desktop:tw-flex'>
              <a className="tw-p-8" href={customer ? "/pages/account-plan" : "/account/login"} onClick={() => logNavInteraction({
                ...BASE_NAV_INTERACTION_CONFIG,
                section: NAV_SECTIONS.PRIMARY.TOP_BAR.ICONS, 
                order: 1, 
                elementType: NAV_ELEMENT_TYPES.ICON, 
                text: customer ? 'Account' : 'Login', 
                action: NAV_ACTIONS.LINK,
              })}>
                <UserIcon className="tw-size-24 tw-fill-transparent tw-stroke-[#3F4443]" />
              </a>
            </li>
            <li className='tw-p-10 desktop:tw-pr-20'>
              {redirectCartToIndex ? 
                (             
                  <a href="/?showCart=true" onClick={() => logNavInteraction({
                    ...BASE_NAV_INTERACTION_CONFIG,
                    section: NAV_SECTIONS.PRIMARY.TOP_BAR.ICONS, 
                    order: isMobile ? 1 : 2, 
                    elementType: NAV_ELEMENT_TYPES.ICON, 
                    text: 'Cart', 
                    action: NAV_ACTIONS.LINK,
                  })}>
                    <ShoppingCartIcon className="tw-size-24 tw-fill-transparent tw-stroke-[#3F4443]" />
                  </a>
                ) : (
                  <button class="bb-cart-button tw-relative tw-flex tw-items-center tw-gap-[10px]" data-toggle="sitewide-cart"
                  data-cart-icon-with-count aria-label="Show Cart" onClick={() => logNavInteraction({
                    ...BASE_NAV_INTERACTION_CONFIG,
                    section: NAV_SECTIONS.PRIMARY.TOP_BAR.ICONS, 
                    order: isMobile ? 1 : 2, 
                    elementType: NAV_ELEMENT_TYPES.ICON, 
                    text: 'Cart', 
                    action: NAV_ACTIONS.OPEN_SUBMENU,
                  })}>
                    <ShoppingCartIcon className="tw-size-24 tw-fill-transparent tw-stroke-[#3F4443]" />
                    <span className="tw-absolute tw-left-12 tw-top-[-.75rem] tw-flex tw-h-20 tw-w-min tw-min-w-[20px] tw-items-center tw-justify-center tw-rounded-full tw-bg-[linear-gradient(220deg,_#005EB8_19.92%,_#0067BE_27.68%,_#006FC3_35.44%,_#0077C8_43.2%,_#007ECC_50.96%,_#0086D1_58.72%,_#008ED5_66.48%,_#0096D9_74.23%,_#009DDD_81.99%,_#00A5E0_89.75%)] tw-p-[5px] tw-text-left tw-text-[.75rem] tw-font-bold tw-leading-[14px] tw-text-white" data-cart-item-count=""></span>
                  </button>
                )}
            </li>
          </ul>
          
          {/* Mobile Toggle */}
            {(isMobile || isTablet) && (
              <div className="tw-flex">
                <button
                  aria-label="Mobile menu"
                  aria-expanded={drawerState.nav}
                  onClick={() => handleNavToggle({
                    section: NAV_SECTIONS.PRIMARY.MOBILE.MENU_TOGGLE, 
                    order: 1, 
                    elementType: NAV_ELEMENT_TYPES.ICON, 
                    text: drawerState.nav ? 'Close' : 'Open',
                    action: NAV_ACTIONS.OPEN_SUBMENU,
                  })}
                  className="tw-block tw-p-10 tw-text-gray-900 desktop:tw-hidden"
                >
                  <Bars3Icon className="tw-size-[24px]" />
                </button>
              </div>
            )}
        </div>


        {(isMobile || isTablet) && (
          <>
            <NavDrawer
              ref={navDrawerRef}
              isOpen={drawerState.nav}
              onOpenSubDrawer={openSubDrawer}
              onCloseNavDrawer={closeBothDrawers}
              customer={customer}
              activeSubDrawerKey={activeSubDrawerKey}
              menuItems={menuItems}
            />
            
            <SubDrawer
              ref={subDrawerRef}
              isOpen={drawerState.sub}
              onClose={closeSubDrawer}
              navItem={activeMenuItem}
              activeSubDrawerKey={activeSubDrawerKey}
              onCloseNavDrawer={closeBothDrawers}
              onResetScroll={(fn) => setResetScrollFn(() => fn)}
              menuItems={menuItems} 
            />
          </>
        )}
      </nav>
      
      {(!isMobile && !isTablet) && activeMenuItem && (
        <DropdownMenuItem
          isOpen={activeMenuItem}
          link={activeMenuItem}
          ref={dropdownRef}
          onClose={closeAllMenus}
          onMouseEnter={() => handleMenuHover(currentMenu)}
          onMouseLeave={handleMenuLeave}
          promoData={activeMenuItem?.promo_cards || []}
          featuredProducts={activeMenuItem.featured_products || []}
          displayNewsletter={activeMenuItem.display_newsletter || false}
          categoryGroups={activeMenuItem.category_groups || []}
          featuredLinks={activeMenuItem.featured_links || []}
          numberOfColumns={activeMenuItem.number_of_columns || 3}
          featuredProductsNavSectionName={activeMenuItem.featured_products_section_name}
          newsletterNavSectionName={activeMenuItem.newsletter_form_section_name}
          featuredLinksNavSectionName={activeMenuItem.featured_links_section_name}
          dropdownContentNavSectionName={activeMenuItem.category_groups_section_name}
          featuredPromoCardsNavSectionName={activeMenuItem.promo_cards_section_name}
        />
      )}
    </>
  );
};

export default Header;
